import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '@core/store';
import { logout } from '@core/store/slices/authSlice';
import { setMobileMenuOpen, setCartOpen } from '@core/store/slices/uiSlice';
import { cn } from '@shared/utils';

/**
 * 100% PARITY VERSION: Replicating Angular fp-navbar exactly
 */
export function Header() {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const searchInputRef = useRef<HTMLInputElement>(null);

    const [searchExpanded, setSearchExpanded] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [accountMenuOpen, setAccountMenuOpen] = useState(false);
    const [isSticky, setIsSticky] = useState(false);

    const { isUserAuthenticated, user } = useAppSelector((state) => state.auth);
    const { totalItems } = useAppSelector((state) => state.cart);
    const { mobileMenuOpen } = useAppSelector((state) => state.ui);


    useEffect(() => {
        // Use IntersectionObserver to handle sticky header without scroll listeners
        const sentinel = document.createElement('div');
        sentinel.style.position = 'absolute';
        sentinel.style.top = '50px';
        sentinel.style.width = '1px';
        sentinel.style.height = '1px';
        sentinel.style.pointerEvents = 'none';
        sentinel.setAttribute('data-header-sentinel', '');
        document.body.prepend(sentinel);

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry) {
                    setIsSticky(!entry.isIntersecting);
                }
            },
            { threshold: 0 }
        );

        observer.observe(sentinel);

        return () => {
            observer.disconnect();
            sentinel.remove();
        };
    }, []);

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (!(e.target as HTMLElement).closest('.account-dropdown')) {
                setAccountMenuOpen(false);
            }
        };
        document.addEventListener('click', handleClick);
        return () => document.removeEventListener('click', handleClick);
    }, []);

    const toggleSearch = () => {
        setSearchExpanded(!searchExpanded);
        if (!searchExpanded) {
            setTimeout(() => searchInputRef.current?.focus(), 100);
        }
    };

    const handleSearch = (e: React.FormEvent | React.KeyboardEvent) => {
        if ('key' in e && e.key !== 'Enter') return;
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
            setSearchQuery('');
            setSearchExpanded(false);
        }
    };

    const handleLogout = () => {
        dispatch(logout());
        setAccountMenuOpen(false);
        if (mobileMenuOpen) dispatch(setMobileMenuOpen(false));
        navigate('/');
    };

    const toggleMobileMenu = () => {
        dispatch(setMobileMenuOpen(!mobileMenuOpen));
    };

    const navLinks = [
        { href: '/', label: 'HOME' },
        { href: '/shop', label: 'SHOP' },
        { href: '/brands', label: 'BRANDS' },
        { href: '/bespoke-services', label: 'BESPOKE SERVICES' },
        { href: '/about-us', label: 'ABOUT US' },
        { href: '/contact-us', label: 'CONTACT US' },
    ];

    return (
        <nav className={cn("boostingyourlifestyle-navbar", isSticky && "sticky-nav")}>
            <div className="nav-container">
                {/* Left: Logo & Toggle */}
                <div className="nav-left flex items-center gap-4 lg:gap-8">
                    <Link to="/" className="boostingyourlifestyle-logo shrink-0">
                        <span className="main-brand text-2xl md:text-3xl">Boosting Your Lifestyle</span>
                    </Link>
                    <div className="menu-toggle !hidden md:!flex items-center gap-2 cursor-pointer group" onClick={toggleMobileMenu}>
                        <span className="material-symbols-outlined group-hover:text-[#C4A661] transition-colors">menu</span>
                        <span className="toggle-text text-[11px] font-bold tracking-widest group-hover:text-[#C4A661] transition-colors">BRANDS & COLLECTIONS</span>
                    </div>
                    {/* Mobile Menu Icon Only */}
                    <button className="md:!hidden flex items-center justify-center w-10 h-10" onClick={toggleMobileMenu}>
                        <span className="material-symbols-outlined text-2xl">menu</span>
                    </button>
                </div>

                {/* Center: Navigation Links */}
                <div className="nav-center !hidden lg:!flex items-center justify-center gap-8">
                    {navLinks.map((link) => (
                        <Link
                            key={link.href}
                            to={link.href}
                            className={cn(
                                "text-[11px] font-bold tracking-[0.2em] transition-all hover:text-[#C4A661]",
                                location.pathname === link.href && "text-[#C4A661] active"
                            )}
                        >
                            {link.label}
                        </Link>
                    ))}
                </div>

                {/* Right: Icons */}
                <div className="nav-right flex items-center shrink-0">
                    {/* Search Input */}
                    <div className={cn("nav-search flex items-center", searchExpanded && "expanded")}>
                        <div className={cn("overflow-hidden transition-all duration-300 flex items-center", searchExpanded ? "w-[180px] md:w-[250px] opacity-100" : "w-0 opacity-0")}>
                            <input
                                ref={searchInputRef}
                                type="text"
                                placeholder="Search..."
                                className="bg-transparent border-b border-gray-200 focus:border-[#C4A661] outline-none text-sm w-full py-1"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={handleSearch}
                            />
                        </div>
                        <button className="icon-btn search-btn p-2" onClick={toggleSearch}>
                            <span className="material-symbols-outlined text-[24px]">{searchExpanded ? 'close' : 'search'}</span>
                        </button>
                    </div>

                    {/* Hide other icons on mobile when search is expanded to prevent overflow */}
                    <div className={cn("flex items-center transition-all duration-200", searchExpanded ? "hidden xs:flex opacity-0 xs:opacity-100" : "flex opacity-100")}>
                        {/* Cart Bag */}
                        <button className="icon-btn bag-btn relative p-2" onClick={() => dispatch(setCartOpen(true))}>
                            <span className="material-symbols-outlined text-[26px]">shopping_bag</span>
                            {totalItems > 0 && (
                                <span className="absolute top-1 right-1 bg-[#C4A661] text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center shadow-sm">
                                    {totalItems}
                                </span>
                            )}
                        </button>

                        {/* Account/Login */}
                        <div className="account-dropdown relative ml-1 md:ml-2">
                            {isUserAuthenticated ? (
                                <>
                                    <button className="icon-btn account-btn p-2" onClick={(e) => { e.stopPropagation(); setAccountMenuOpen(!accountMenuOpen); }}>
                                        <span className="material-symbols-outlined text-[26px]">account_circle</span>
                                    </button>
                                    {accountMenuOpen && (
                                        <div className="account-menu">
                                            <div className="account-menu-header">
                                                <span className="user-name">{user?.firstName} {user?.lastName}</span>
                                                <span className="welcome-text">Welcome back!</span>
                                            </div>
                                            <div className="account-menu-links">
                                                <Link to="/my-account" onClick={() => setAccountMenuOpen(false)}>
                                                    <span className="material-symbols-outlined">person</span>
                                                    My Account
                                                </Link>
                                                <button className="logout-btn" onClick={handleLogout}>
                                                    <span className="material-symbols-outlined">logout</span>
                                                    Logout
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <Link to="/authentication" className="login-btn flex items-center gap-1.5 px-3 py-1.5 md:px-4 md:py-2 bg-gray-50 hover:bg-gray-100 border border-gray-100 rounded-md transition-all">
                                    <span className="material-symbols-outlined text-[20px]">person</span>
                                    <span className="login-text text-[11px] font-bold tracking-widest hidden sm:inline">LOGIN</span>
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile/Sidebar Overlay */}
            {mobileMenuOpen && (
                <div className="boostingyourlifestyle-mobile-overlay">
                    <button
                        className="close-overlay icon-btn"
                        onClick={() => dispatch(setMobileMenuOpen(false))}
                    >
                        <span className="material-symbols-outlined !text-[32px]">close</span>
                    </button>

                    <div className="overlay-content py-10 w-full">
                        <div className="flex flex-col items-center gap-6">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.href}
                                    to={link.href}
                                    className="text-2xl font-serif-lux"
                                    onClick={() => dispatch(setMobileMenuOpen(false))}
                                >
                                    {link.label}
                                </Link>
                            ))}
                        </div>

                        <hr className="my-8 mx-auto w-1/4 opacity-30" />

                        <div className="flex flex-col items-center gap-6">
                            {isUserAuthenticated ? (
                                <>
                                    <Link
                                        to="/my-account"
                                        className="text-xl font-sans-lux"
                                        onClick={() => dispatch(setMobileMenuOpen(false))}
                                    >
                                        MY ACCOUNT
                                    </Link>
                                    <button
                                        className="text-xl font-sans-lux text-red-500"
                                        onClick={handleLogout}
                                    >
                                        LOGOUT
                                    </button>
                                </>
                            ) : (
                                <Link
                                    to="/authentication"
                                    className="text-xl font-sans-lux"
                                    onClick={() => dispatch(setMobileMenuOpen(false))}
                                >
                                    LOGIN / REGISTER
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </nav>
    );
}

export default Header;
